import * as React from 'react';
import { Link } from '@inertiajs/react';
import { Category } from '../../types/landing';
import { CategoryIcon } from '../products/categoryIcons';
import { route } from '../../utils/route';

interface CategoriesSectionProps {
  categories: Category[];
}

// Background colors for variety
const categoryColors = [
  '#e8f5e8', // green light
  '#fff3e0', // orange light
  '#e3f2fd', // blue light
  '#fce4ec', // pink light
  '#f3e5f5', // purple light
  '#e0f7fa', // cyan light
  '#fff8e1', // amber light
  '#e8eaf6', // indigo light
];

export function CategoriesSection({ categories }: CategoriesSectionProps): React.ReactElement {
  return (
    <section className="categories-section">
      <style>{`
        .categories-section {
          padding: 50px 0;
          background: #fff;
        }

        .categories-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }

        .categories-header {
          text-align: center;
          margin-bottom: 30px;
        }

        .categories-title {
          font-size: 28px;
          font-weight: 700;
          color: #333;
          margin-bottom: 10px;
        }

        .categories-subtitle {
          font-size: 16px;
          color: #666;
        }

        .categories-grid {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 16px;
        }

        .category-card {
          width: 220px;
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 18px 20px;
          background: #f8f9fa;
          border-radius: 12px;
          text-decoration: none;
          transition: all 0.2s;
          border: 2px solid transparent;
        }

        .category-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          border-color: #5cb85c;
          text-decoration: none;
        }

        .category-card-icon {
          width: 46px;
          height: 46px;
          min-width: 46px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
        }


        .category-card-content {
          flex: 1;
          min-width: 0;
        }

        .category-card-name {
          font-size: 15px;
          font-weight: 600;
          color: #333;
          line-height: 1.2;
        }

        .category-card-badge {
          display: inline-block;
          font-size: 10px;
          font-weight: 600;
          color: #fff;
          background: #5cb85c;
          padding: 2px 6px;
          border-radius: 4px;
          margin-top: 4px;
          text-transform: uppercase;
        }

        .category-card-arrow {
          color: #bbb;
          font-size: 14px;
          transition: transform 0.2s, color 0.2s;
        }

        .category-card:hover .category-card-arrow {
          color: #5cb85c;
          transform: translateX(4px);
        }

        /* Mobile styles */
        @media (max-width: 768px) {
          .categories-section {
            padding: 32px 0;
          }

          .categories-title {
            font-size: 22px;
          }

          .categories-subtitle {
            font-size: 14px;
          }

          .categories-grid {
            gap: 10px;
          }

          .category-card {
            width: calc(50% - 5px);
            padding: 14px;
            gap: 10px;
          }

          .category-card-icon {
            width: 38px;
            height: 38px;
            min-width: 38px;
          }

          .category-card-name {
            font-size: 13px;
          }

          .category-card-arrow {
            display: none;
          }
        }

        @media (max-width: 480px) {
          .categories-grid {
            gap: 8px;
          }

          .category-card {
            width: calc(50% - 4px);
            padding: 12px;
            flex-direction: column;
            text-align: center;
            gap: 8px;
          }

          .category-card-icon {
            margin: 0 auto;
          }
        }
      `}</style>

      <div className="categories-container">
        <div className="categories-header">
          <h2 className="categories-title">Explorá por Categoría</h2>
          <p className="categories-subtitle">
            Encontrá fácilmente lo que necesitás navegando nuestras categorías
          </p>
        </div>

        <div className="categories-grid">
          {categories.map((category, idx) => (
            <Link
              key={category.id}
              href={route('products.index', { cat: category.slug })}
              className="category-card"
            >
              <div
                className="category-card-icon"
                style={{ background: categoryColors[idx % categoryColors.length] }}
              >
                <CategoryIcon slug={category.slug} size={24} strokeWidth={1.8} color="#444" />
              </div>
              <div className="category-card-content">
                <div className="category-card-name">{category.name}</div>
                {category.badge && (
                  <span className="category-card-badge">{category.badge}</span>
                )}
              </div>
              <i className="fa fa-chevron-right category-card-arrow"></i>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
